import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import api from '@/api/client'
import { useAuth } from '@/hooks/use-auth'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { HelpBanner } from '@/components/ui/help-banner'
import type { PaginatedResponse, TenantResponse, UserResponse, RoleResponse, AssignmentResponse } from '@/api/types'

export function DashboardPage() {
  const { t } = useTranslation(['common', 'tenants'])
  const { user } = useAuth()
  const isPlatformAdmin = user?.platform_role === 'PLATFORM_ADMIN'

  const { data: tenants } = useQuery({
    queryKey: ['tenants', 'count'],
    queryFn: () => api.get<PaginatedResponse<TenantResponse>>('/tenants?limit=1'),
    enabled: isPlatformAdmin,
  })

  const { data: users } = useQuery({
    queryKey: ['users', 'count'],
    queryFn: () => api.get<PaginatedResponse<UserResponse>>('/users?limit=1'),
  })

  const { data: roles } = useQuery({
    queryKey: ['roles', 'count'],
    queryFn: () => api.get<PaginatedResponse<RoleResponse>>('/roles?limit=1'),
  })

  const { data: assignments } = useQuery({
    queryKey: ['assignments', 'count'],
    queryFn: () => api.get<PaginatedResponse<AssignmentResponse>>('/assignments?limit=1'),
  })

  const stats = [
    ...(isPlatformAdmin ? [{ labelKey: 'entities.tenants', value: tenants?.data.total ?? '-' }] : []),
    { labelKey: 'entities.users', value: users?.data.total ?? '-' },
    { labelKey: 'entities.roles', value: roles?.data.total ?? '-' },
    { labelKey: 'entities.assignments', value: assignments?.data.total ?? '-' },
  ]

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-6">{t('nav.dashboard')}</h2>
      <HelpBanner storageKey="dashboard">
        <p>{t('dashboard.help.line1', { ns: 'tenants' })}</p>
        <p>{t('dashboard.help.line2', { ns: 'tenants' })}</p>
      </HelpBanner>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <Card key={stat.labelKey}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {t(stat.labelKey)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stat.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}
